import {
  ConfirmationModal,
  Form,
  FormMultiLineTextInput,
  FormTextInput,
  SubTitle,
  TargetGroupDropdownSelector,
} from 'ck-components';
import {
  PostAlarm,
  QK,
  translateTargetGroupNicely,
  useAlarmCreate,
} from 'ck-queries';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  getSavedAlarmValues,
  localStorageTyped,
  useTypedNavigate,
} from 'ck-utils';
import { useEffect, useMemo, useState } from 'react';

import { TargetAudienceCount } from 'src/components/TargetAudienceCount';
import { TargetDivisionDropdownSelector } from 'src/components/Form/TargetDivisionDropdownSelector/TargetDivisionDropdownSelector';
import { defaultAlarmValues } from './defaults';
import { useQueryTargetGroupsAudienceCount } from 'src/queries/targetGroups/useQueryTargetGroupsAudience';

interface Props {
  prefill?: PostAlarm;
  isCopy?: boolean;
}

/**
 * The form used to create push notifications.
 */

export function CreateAlarmnForm({ prefill, isCopy }: Props) {
  // Hooks
  const { navigate } = useTypedNavigate();

  const M_CreateAlarm = useAlarmCreate();

  const form = useForm<PostAlarm>({
    mode: 'onChange',
    defaultValues: getSavedAlarmValues(),
  });
  useEffect(() => {
    if (prefill) {
      // If prefill is provided, set the form values
      form.reset(prefill);
    }
  }, [prefill, form]);
  const { reset, watch } = form;

  const targetGroup = watch('targetGroup');
  const division = watch('targetGroup.divisionName');
  const targetGroupName = watch('targetGroup.group');

  const Q_TargetGroupsAudienceCount =
    useQueryTargetGroupsAudienceCount(division);
  // Refetch audience count when division changes
  useEffect(() => {
    Q_TargetGroupsAudienceCount.refetch({
      queryKey: [QK.targetGroupsAudienceCount, division],
    });
  }, [division]);

  // Update Count based on selected target group
  const count = useMemo(() => {
    const data = Q_TargetGroupsAudienceCount.data;
    if (!data) return 0;

    const newCount =
      data.find(({ group }) => group === targetGroupName)?.count ?? 0;
    form.setValue('targetGroup.count', newCount);
    return newCount;
  }, [Q_TargetGroupsAudienceCount.data, targetGroupName, division]);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const cleanForm = () => {
    localStorageTyped.remove('FORM_CREATE_ALARM');
    reset(defaultAlarmValues);
  };

  const onSubmit: SubmitHandler<PostAlarm> = async (data) => {
    M_CreateAlarm.mutateAsync(data).then(() => {
      cleanForm();
      navigate('/push-notifications/archive');
    });
  };

  return (
    <div className='max-w-7xl'>
      <Form
        formLocalStorageKey={!isCopy ? 'FORM_CREATE_ALARM' : undefined}
        onSubmit={() => setIsConfirmationOpen(true)}
        buttonType='button'
        submitButtonLabel={'Gå vidare'}
        submitButtonStyle='w-52'
        onCleanForm={cleanForm}
        form={form}
      >
        <ConfirmationModal
          isOpen={isConfirmationOpen}
          setIsOpen={setIsConfirmationOpen}
          onConfirmation={form.handleSubmit(onSubmit)}
          modalTitle={'Skicka larm'}
          confirmationLabel={'Skicka nu'}
          isDelete
        >
          {targetGroup && (
            <p className='max-w-md text-lg'>
              Du håller på att skicka en notis med <b>hög prio</b> som kommer
              att skickas till
              {` ${translateTargetGroupNicely(targetGroup)}`}. Är du säker på
              att du vill skicka larmet?
            </p>
          )}
        </ConfirmationModal>
        <div className='flex flex-1 flex-col gap-6 md:flex-row md:gap-14'>
          <div className='flex-1'>
            <SubTitle label='Svenska' />
            <FormTextInput
              label={'RUBRIK'}
              parentForm={form}
              maxLength={40}
              name='titleSwedish'
            />
            <FormTextInput
              description={'Visas i pushnotisen'}
              label={'KORT MEDDELANDE'}
              parentForm={form}
              maxLength={100}
              name='notificationMessageSwedish'
            />
            <FormMultiLineTextInput
              label={'LÅNGT MEDDELANDE'}
              description={'Visas i appens inkorgsvy'}
              parentForm={form}
              type='textfield'
              maxLength={1000}
              name='inboxMessageSwedish'
            />
          </div>
          <div className='flex-1'>
            <SubTitle label='English' />
            <FormTextInput
              label={'TITLE'}
              parentForm={form}
              maxLength={40}
              name='titleEnglish'
            />
            <FormTextInput
              label={'SHORT MESSAGE'}
              description={'Displayed in the push notification'}
              parentForm={form}
              maxLength={100}
              name='notificationMessageEnglish'
            />
            <FormMultiLineTextInput
              label={'LONG MESSAGE'}
              description={`Displayed in the app's inbox`}
              parentForm={form}
              maxLength={1000}
              name='inboxMessageEnglish'
            />
          </div>
        </div>
        <hr />
        <div className='flex flex-col gap-6 md:flex-row md:gap-14'>
          <div className='flex-1'>
            <TargetGroupDropdownSelector name='targetGroup.group' form={form} />
          </div>
          {/* Invisible filler to get column width correct */}
          <div className='hidden flex-1 md:block' />
        </div>
        <div className='mt-[-40px] flex flex-row gap-14'>
          <div className='flex-1'>
            {/* Uncomment when we want to specify division
            <TargetDivisionDropdownSelector
              name='targetGroup.divisionName'
              form={form}
            /> */}
          </div>
          {/* Invisible filler to get column width correct */}
          <div className='hidden flex-1 md:block' />
        </div>
        <div className='mt-[-40px] mb-4 flex flex-row gap-14'>
          <TargetAudienceCount count={count} />
          <div className='hidden flex-1 md:block'></div>
        </div>
        <div className='flex flex-col gap-6 md:flex-row md:gap-14'>
          <div className='flex-1'>
            <TargetDivisionDropdownSelector
              label='Avsändare / Sender'
              name='createdByDivisionName'
              form={form}
            />
          </div>
          {/* Invisible filler to get column width correct */}
          <div className='hidden flex-1 md:block' />
        </div>
      </Form>
    </div>
  );
}
